import { motion, useReducedMotion, type Variants } from "motion/react";
import type { ReactNode } from "react";

type PageTransitionProps = {
  children: ReactNode;
  className?: string;
  /**
   * When true, direct children fade/slide in with a stagger between them
   * (each child must be wrapped in `<PageTransitionItem>`).
   * Default false — the whole container fades up as one unit.
   */
  stagger?: boolean;
  /** Seconds between staggered children. Default 0.22. */
  staggerDelay?: number;
  /** Seconds before the first staggered child starts. Default 0.08. */
  delayChildren?: number;
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
  },
};

/**
 * Fade + slide-up transition for the main page content. Wrap each page's
 * top-level section element.
 *
 * Set `stagger` to true and wrap each direct child in `<PageTransitionItem>`
 * to get a staggered entrance instead of a single whole-section fade.
 * Override `staggerDelay` / `delayChildren` when you have many children and
 * want the cascade to feel snappier (e.g. a grid of cards).
 */
export function PageTransition({
  children,
  className,
  stagger = false,
  staggerDelay = 0.22,
  delayChildren = 0.08,
}: PageTransitionProps) {
  const reduceMotion = useReducedMotion();
  // Honor the OS "reduce motion" preference: render content in place with no
  // fade/slide/stagger.
  if (reduceMotion) {
    return <section className={className}>{children}</section>;
  }
  if (stagger) {
    const containerVariants: Variants = {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: staggerDelay,
          delayChildren,
        },
      },
    };
    return (
      <motion.div
        className={className}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {children}
      </motion.div>
    );
  }
  return (
    <motion.section
      className={className}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.section>
  );
}

export function PageTransitionItem({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const reduceMotion = useReducedMotion();
  if (reduceMotion) {
    return <div className={className}>{children}</div>;
  }
  return (
    <motion.div className={className} variants={itemVariants}>
      {children}
    </motion.div>
  );
}
